import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import facade from "../services/qwof/apiFacade";
import NavigationRow from "../components/NavigationRow.jsx";
import Spinner from "../components/Spinner.jsx";
import QuagganEmoji from "../components/QuagganEmoji.jsx";
import UnderwaterBackground from "../components/UnderwaterBackground.jsx";

// Scrollbar styling for dark underwater theme and background layering
// TODO: Move to CSS file and ensure it only applies to this page
const scrollbarStyles = `
  .guild-member-scrollable::-webkit-scrollbar {
    width: 10px;
  }
  .guild-member-scrollable::-webkit-scrollbar-track {
    background: rgba(0, 26, 46, 0.3);
    border-radius: 5px;
  }
  .guild-member-scrollable::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, #62d0ff, #90e0ef);
    border-radius: 5px;
    border: 2px solid rgba(0, 26, 46, 0.8);
  }
  .guild-member-scrollable::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(to bottom, #90e0ef, #a8dadc);
  }
  .guild-member-scrollable {
    scrollbar-color: #62d0ff rgba(0, 26, 46, 0.3);
    scrollbar-width: thin;
  }
  /* Ensure underwater background sits behind content and is non-interactive */
  .underwater-bg.page-underwater {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 0 !important;
    pointer-events: none !important;
  }
  /* Make main content render above the background */
  .guild-member-scrollable, .nav-row, .page-content {
    position: relative;
    z-index: 1;
  }
`;

function GuildMemberDetailPage() {
  // Add scrollbar styles to document
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = scrollbarStyles;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  const { gw2Username } = useParams();
  const decodedUsername = decodeURIComponent(gw2Username || "");
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [hasAccess, setHasAccess] = useState(false);
  const [member, setMember] = useState(null);
  const guildId = "DA835590-BFBA-4D4C-BF10-014905F32E00";
  const [originalAliases, setOriginalAliases] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ message: "", tone: "" });
  const [newAliasInput, setNewAliasInput] = useState("");
  const [isPreferredInput, setIsPreferredInput] = useState(false);
  const [aliasError, setAliasError] = useState("");
  const [allMembersCache, setAllMembersCache] = useState([]);

  // Normalize a guild member DTO from the API into the shape used by this page
  const normalizeMember = (m) => {
    if (!m) return m;
    const preferredId = m?.preferredAliasId;
    const aliases = Array.isArray(m.aliases)
      ? m.aliases.map((a) => ({
          id: a.id,
          name: resolveAliasName(a),
          ignored: a.isIgnored || a.ignored || false,
          preferred: preferredId ? a.id === preferredId : !!a.preferred,
        }))
      : [];
    return { ...m, aliases };
  };

  const aliasDisplayName = (alias) => alias?.name || alias?.aliasName || "";
  const resolveAliasName = (alias) =>
    alias?.name ||
    alias?.aliasName ||
    alias?.alias ||
    alias?.displayName ||
    alias?.value ||
    "";

  // Check access on mount
  useEffect(() => {
    const hasRequiredRole = isLoggedIn && facade.hasAnyRole(["LEADER", "ADMIN"]);
    setHasAccess(hasRequiredRole);
  }, [isLoggedIn]);

  // Load guild member data
  useEffect(() => {

    if (!hasAccess) {
      setLoading(false);
      return;
    }

    if (!decodedUsername) {
      setError("No member specified");
      setLoading(false);
      return;
    }

    // If member is already loaded, don't reload
    if (member) {
      return;
    }

    const loadMemberData = async () => {
      setLoading(true);
      setError("");

      try {
        // Hydrate from the full guild member list so the alias cache is complete.
        const members = await facade.getGuildMembers(guildId);
        const membersArray = Array.isArray(members) ? members : [];
        if (membersArray.length === 0) {
          setError("No guild members found");
          setLoading(false);
          return;
        }

        const normalizedMembers = membersArray.map((memberItem) => normalizeMember(memberItem));
        setAllMembersCache(normalizedMembers);

        const normalized = normalizedMembers.find((memberItem) => memberItem?.gw2Username === decodedUsername);
        if (!normalized) {
          setError(`Guild member "${decodedUsername}" not found`);
          setLoading(false);
          return;
        }
        setMember(normalized);
        setOriginalAliases(Array.isArray(normalized?.aliases) ? normalized.aliases : []);
        
        setLoading(false);
      } catch (err) {
        const msg = err?.fullError || err?.message || "Unknown error";
        console.error("[GuildMemberDetailPage] Error loading guild member:", err);
        setError(`Failed to load guild member: ${msg}`);
        setLoading(false);
      }
    };

    loadMemberData().catch((err) => {
      console.error("Uncaught error in loadMemberData:", err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, decodedUsername]);

  // removed verbose debug logging

  const handleAddAlias = async () => {
    setAliasError("");

    if (!newAliasInput.trim()) {
      setAliasError("Alias name cannot be empty");
      return;
    }

    const aliasTrim = newAliasInput.trim();

    // Check if alias is already in use by any other member (server-backed)
    const aliasLower = aliasTrim.toLowerCase();

    const duplicateOwner = allMembersCache.find((guildMember) => {
      if (guildMember?.gw2Username === decodedUsername) {
        return false;
      }

      const aliases = Array.isArray(guildMember?.aliases) ? guildMember.aliases : [];
      return aliases.some((alias) => resolveAliasName(alias).toLowerCase() === aliasLower);
    });

    if (duplicateOwner) {
      setAliasError(`Alias is already taken by ${duplicateOwner.gw2Username || "Unknown"}`);
      return;
    }

    const currentAliases = Array.isArray(member?.aliases) ? member.aliases : [];
    if (currentAliases.some((alias) => resolveAliasName(alias).toLowerCase() === aliasLower)) {
      setAliasError("Alias is already added to this member");
      return;
    }

    // Add to local state
    const newAlias = {
      name: aliasTrim,
      preferred: isPreferredInput,
      ignored: false,
    };

    setMember((prev) => ({
      ...prev,
      aliases: [...(Array.isArray(prev?.aliases) ? prev.aliases : []), newAlias],
    }));

    setNewAliasInput("");
    setIsPreferredInput(false);
  };

  const handleRemoveAlias = (index) => {
    setMember((prev) => ({
      ...prev,
      aliases: (Array.isArray(prev?.aliases) ? prev.aliases : []).filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handleSetPreferred = (index) => {
    setMember((prev) => {
      const aliases = Array.isArray(prev?.aliases) ? prev.aliases : [];
      const targetAlias = aliases[index] || null;
      return {
        ...prev,
        preferredAliasId: targetAlias?.id ?? null,
        aliases: aliases.map((alias, i) => ({
          ...alias,
          preferred: i === index,
        })),
      };
    });
  };

  const handleSubmit = async () => {
    if (!member?.gw2Username) {
      setSubmitStatus({ message: "Member username not found", tone: "error" });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ message: "", tone: "" });

    try {
      const currentAliases = Array.isArray(member?.aliases) ? member.aliases : [];
      const originalAliasList = Array.isArray(originalAliases) ? originalAliases : [];

      const currentIds = new Set(currentAliases.map((alias) => alias?.id).filter(Boolean));

      const newAliases = currentAliases
        .filter((alias) => !alias?.id)
        .map((alias) => ({
          aliasName: aliasDisplayName(alias),
          isIgnored: alias.ignored || false,
        }))
        .filter((alias) => alias.aliasName.trim().length > 0);

      const deletedAliases = originalAliasList
        .filter((alias) => alias?.id && !currentIds.has(alias.id))
        .map((alias) => ({ id: alias.id }));

      if (deletedAliases.length > 0) {
        await facade.deleteGuildAliases(member.gw2Username, deletedAliases);
      }

      if (newAliases.length > 0) {
        await facade.addGuildAliases(member.gw2Username, newAliases);
      }

      // Refresh member from server to pick up created ids and authoritative data
      const fresh = await facade.getGuildMember(guildId, member.gw2Username);
      if (fresh) {
        const normalizedFresh = normalizeMember(fresh);
        setMember(normalizedFresh);
        setOriginalAliases(Array.isArray(normalizedFresh?.aliases) ? normalizedFresh.aliases : []);
      }

      setSubmitStatus({
        message: "Guild member updated successfully",
        tone: "success",
      });

      setTimeout(() => {
        setSubmitStatus({ message: "", tone: "" });
      }, 2500);
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setSubmitStatus({
        message: `Failed to update: ${msg}`,
        tone: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasAccess) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <NavigationRow />
        
        {/* no debug panel */}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#ff6b6b", marginBottom: 16 }}>
            You do not have permission to view this page
          </h2>
          <QuagganEmoji type="failure" size={120} />
          <button
            onClick={() => navigate("/")}
            style={{
              marginTop: 24,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#62d0ff",
              color: "#001a2e",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <NavigationRow />
        
        {/* no debug panel */}

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spinner size={100} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <NavigationRow />
        
        {/* no debug panel */}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#ff6b6b", marginBottom: 16 }}>{error}</h2>
          <QuagganEmoji type="failure" size={100} />
          <button
            onClick={() => navigate("/admin/members")}
            style={{
              marginTop: 24,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#62d0ff",
              color: "#001a2e",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Back to Members
          </button>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <NavigationRow />
        
        {/* no debug panel */}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#90e0ef", marginBottom: 16 }}>Member not found</h2>
          <button
            onClick={() => navigate("/admin/members")}
            style={{
              marginTop: 16,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#62d0ff",
              color: "#001a2e",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Back to Members
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
      <UnderwaterBackground className="page-underwater" />
      <NavigationRow />

      <div
        className="guild-member-scrollable"
        style={{
          flex: 1,
          overflow: "auto",
          padding: "32px 20px",
          maxWidth: 800,
          margin: "0 auto",
          width: "100%",
        }}
      >
        <button
          onClick={() => navigate("/admin/members")}
          style={{
            marginBottom: 24,
            padding: "8px 16px",
            borderRadius: 6,
            border: "1px solid rgba(144, 224, 239, 0.5)",
            background: "transparent",
            color: "#62d0ff",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(98, 208, 255, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          ← Back to Members
        </button>

        <div
          style={{
            background: "rgba(8, 24, 50, 0.6)",
            borderRadius: 12,
            padding: 24,
            border: "1px solid rgba(144, 224, 239, 0.2)",
          }}
        >
          <h2 style={{ color: "#90e0ef", marginBottom: 8 }}>
            {member?.gw2Username || "Unknown Member"}
          </h2>
          <p style={{ color: "rgba(207, 238, 255, 0.6)", marginBottom: 24, fontSize: 13 }}>
            Rank: {member?.rank || "-"} • Joined:{" "}
            {member?.joined
              ? new Date(member.joined[0], member.joined[1] - 1, member.joined[2]).toLocaleDateString()
              : "-"}
          </p>

          <hr style={{ margin: "24px 0", borderColor: "rgba(144, 224, 239, 0.2)" }} />

          <h3 style={{ color: "#62d0ff", marginBottom: 16 }}>Manage Aliases</h3>

          {/* Add Alias Form */}
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: "rgba(8, 24, 50, 0.4)",
              borderRadius: 8,
              border: "1px solid rgba(144, 224, 239, 0.15)",
            }}
          >
            <label style={{ display: "block", color: "#cfeeff", marginBottom: 8, fontSize: 13 }}>
              Add New Alias
            </label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                placeholder="Enter alias name"
                value={newAliasInput}
                onChange={(e) => setNewAliasInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddAlias()}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid rgba(144, 224, 239, 0.3)",
                  background: "rgba(8, 24, 50, 0.6)",
                  color: "#cfeeff",
                  fontSize: 14,
                }}
              />
              <button
                type="button"
                onClick={handleAddAlias}
                disabled={isSubmitting}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: "#62d0ff",
                  color: "#001a2e",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: 13,
                  opacity: isSubmitting ? 0.6 : 1,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.currentTarget.style.background = "#3ab3d9";
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.currentTarget.style.background = "#62d0ff";
                }}
              >
                Add Alias
              </button>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#cfeeff",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={isPreferredInput}
                onChange={(e) => setIsPreferredInput(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              Set as preferred
            </label>

            {aliasError && (
              <div
                style={{
                  marginTop: 8,
                  padding: 8,
                  background: "rgba(255, 107, 107, 0.1)",
                  borderRadius: 6,
                  color: "#ff9999",
                  fontSize: 12,
                }}
              >
                {aliasError}
              </div>
            )}
          </div>

          {/* Aliases List */}
          {Array.isArray(member?.aliases) && member.aliases.length > 0 ? (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ color: "#90e0ef", marginBottom: 12, fontSize: 14 }}>
                Current Aliases ({member.aliases.length})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {member.aliases.map((alias, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 12,
                      background: "rgba(8, 24, 50, 0.4)",
                      borderRadius: 6,
                      border: `1px solid ${
                        alias?.preferred
                          ? "rgba(98, 208, 255, 0.5)"
                          : "rgba(144, 224, 239, 0.15)"
                      }`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span
                        style={{
                          color: "#cfeeff",
                          flex: 1,
                          fontWeight: alias?.preferred ? 600 : 400,
                        }}
                      >
                        {aliasDisplayName(alias)}
                      </span>
                      {alias?.preferred && (
                        <span
                          style={{
                            background: "rgba(98, 208, 255, 0.2)",
                            color: "#62d0ff",
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          PREFERRED
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {!alias?.preferred && (
                        <button
                          onClick={() => handleSetPreferred(index)}
                          disabled={isSubmitting}
                          style={{
                            padding: "4px 12px",
                            borderRadius: 4,
                            border: "1px solid rgba(144, 224, 239, 0.3)",
                            background: "transparent",
                            color: "#90e0ef",
                            cursor: isSubmitting ? "not-allowed" : "pointer",
                            fontWeight: 500,
                            fontSize: 12,
                            opacity: isSubmitting ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isSubmitting)
                              e.currentTarget.style.background =
                                "rgba(144, 224, 239, 0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          Set Preferred
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveAlias(index)}
                        disabled={isSubmitting}
                        style={{
                          padding: "4px 12px",
                          borderRadius: 4,
                          border: "none",
                          background: "rgba(255, 107, 107, 0.1)",
                          color: "#ff9999",
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                          fontWeight: 600,
                          fontSize: 12,
                          opacity: isSubmitting ? 0.6 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting)
                            e.currentTarget.style.background =
                              "rgba(255, 107, 107, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(255, 107, 107, 0.1)";
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                marginBottom: 24,
                padding: 16,
                textAlign: "center",
                color: "rgba(207, 238, 255, 0.5)",
                fontSize: 13,
              }}
            >
              No aliases yet. Add one to get started.
            </div>
          )}

          <hr style={{ margin: "24px 0", borderColor: "rgba(144, 224, 239, 0.2)" }} />

          {/* Submit Status */}
          {submitStatus.message && (
            <div
              style={{
                marginBottom: 16,
                padding: 16,
                borderRadius: 8,
                background:
                  submitStatus.tone === "success"
                    ? "rgba(98, 217, 141, 0.1)"
                    : "rgba(255, 107, 107, 0.1)",
                border: `1px solid ${
                  submitStatus.tone === "success"
                    ? "rgba(98, 217, 141, 0.3)"
                    : "rgba(255, 107, 107, 0.3)"
                }`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      color:
                        submitStatus.tone === "success"
                          ? "#a8e6c1"
                          : "#ff9999",
                      margin: 0,
                      fontSize: 14,
                    }}
                  >
                    {submitStatus.message}
                  </p>
                </div>
                <QuagganEmoji
                  type={submitStatus.tone === "success" ? "success" : "failure"}
                  size={48}
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              width: "100%",
              padding: "12px 24px",
              borderRadius: 8,
              border: "none",
              background: isSubmitting ? "rgba(98, 208, 255, 0.3)" : "#62d0ff",
              color: "#001a2e",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              fontWeight: 700,
              fontSize: 14,
              transition: "all 0.3s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.currentTarget.style.background = "#3ab3d9";
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.currentTarget.style.background = "#62d0ff";
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner compact size={20} />
                Updating...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GuildMemberDetailPage;
